import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { Icon } from '../icon';

import styles from './action-bar.module.scss';

export const ActionBar = (props: ActionBarProps) => {
  const { on, onClear, showBar = true, children, clearable, className, appearance = 'light', ...rest } = props;

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const [animationStep, setAnimationStep] = useState<'width' | 'scale'>('width');

  useEffect(() => {
    if (ref.current) {
      setWidth(ref.current.scrollWidth);
    }
  }, [on, children, clearable, showBar]);

  useEffect(() => {
    if (showBar) {
      setAnimationStep('width');
    }
  }, [on, showBar, children, clearable]);

  return (
    <div className={clsx(styles.actionBar, className, 'action-bar')} {...rest}>
      <AnimatePresence>
        {on && (showBar || clearable) && (
          <motion.div
            key="bar"
            className={clsx(styles.outerHolder, styles[`a-${appearance}`])}
            initial={{ width: 0, scale: 0.5, opacity: 1 }}
            animate={{ width: width + 8, scale: 1, opacity: 1 }}
            exit={{ width: 50, scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut', scale: { duration: 0.1, ease: 'easeOut' } }}
            onAnimationComplete={() => {
              if (animationStep === 'width') {
                setAnimationStep('scale');
              }
            }}
          >
            <motion.div
              ref={ref}
              className={clsx(styles.innerHolder, styles.innerShadow)}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ scale: { duration: 0.1, delay: 0, ease: 'easeOut' } }}
            >
              {showBar && children}
              {clearable && (
                <button
                  type="button"
                  onClick={onClear}
                  aria-label="Clear selections"
                  className={clsx(styles.clearControl, !showBar && styles.clearOnly)}
                >
                  <Icon type="cancel-circle-solid" size={24} />
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export interface ActionBarProps extends PropsWithChildren {
  on?: boolean;
  showBar?: boolean;
  className?: string;
  clearable?: boolean;
  onClear?: () => void;
  appearance?: 'light' | 'dark';
}
